import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X, Eye } from "lucide-react";
import api, { timeAgo, apiError } from "@/lib/api";
import { PageHeader, Table, StatusBadge } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function PRSubmissions() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState("pending_review");
  const [preview, setPreview] = useState<any>(null);

  const { data: subs } = useQuery({
    queryKey: ["admin-pr-subs", status],
    queryFn: async () => (await api.get(`/admin/pr-submissions?status=${status}`)).data,
  });

  const resolve = async (sub: any, action: "approve" | "reject") => {
    try {
      const res = await api.patch(`/admin/pr-submissions/${sub.id}`, { action });
      toast.success(res.data.message);
      queryClient.invalidateQueries({ queryKey: ["admin-pr-subs"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-pr-page">
      <PageHeader title="PR Submissions" sub="Review paid press releases before publishing"
        actions={
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44 bg-[#1A1A1A] border-white/10 text-white" data-testid="pr-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white">
              <SelectItem value="pending_review">Pending review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="draft_unpaid">Unpaid drafts</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        } />

      <Table headers={["Release", "Company", "Paid", "Submitted", "Status", "Actions"]}>
        {(subs || []).map((s: any) => (
          <tr key={s.id} data-testid={`pr-sub-row-${s.id}`}>
            <td className="px-4 py-3 max-w-[300px]">
              {s.status === "approved" && s.article_slug ? (
                <Link to={`/article/${s.article_slug}`} target="_blank"
                  className="text-white font-medium hover:text-[#00E5FF] transition-colors line-clamp-2">{s.title}</Link>
              ) : (
                <p className="text-white font-medium line-clamp-2">{s.title}</p>
              )}
              <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{s.summary}</p>
            </td>
            <td className="px-4 py-3">
              <p className="text-sm text-gray-300">{s.company}</p>
              <p className="font-mono text-[10px] text-gray-500">{s.contact_name}</p>
            </td>
            <td className="px-4 py-3"><StatusBadge status={s.paid ? "paid" : "unpaid"} /></td>
            <td className="px-4 py-3 font-mono text-xs text-gray-400">{timeAgo(s.created_at)}</td>
            <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
            <td className="px-4 py-3">
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" onClick={() => setPreview(s)} data-testid={`pr-preview-${s.id}`}
                  className="border-white/15 text-gray-300 hover:bg-white/5 h-8">
                  <Eye className="h-3.5 w-3.5" />
                </Button>
                {s.status === "pending_review" && (
                  <>
                    <Button size="sm" onClick={() => resolve(s, "approve")} data-testid={`pr-approve-${s.id}`}
                      className="bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25 h-8">
                      <Check className="h-3.5 w-3.5 mr-1" /> Publish
                    </Button>
                    <Button size="sm" onClick={() => resolve(s, "reject")} data-testid={`pr-reject-${s.id}`}
                      className="bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 h-8">
                      <X className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                  </>
                )}
              </div>
            </td>
          </tr>
        ))}
      </Table>
      {(subs || []).length === 0 && (
        <p className="text-gray-500 text-sm mt-6 text-center py-8 border border-dashed border-white/10 rounded-lg">
          No press releases with this status.
        </p>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{preview?.title}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              <p className="font-mono text-[10px] text-gray-500 uppercase">
                {preview.company} · {preview.contact_name}{preview.website ? ` · ${preview.website}` : ""}
              </p>
              {preview.image_url && <img src={preview.image_url} alt="" className="rounded-lg border border-white/10 max-h-52 object-cover" />}
              <p className="text-sm text-gray-300 italic">{preview.summary}</p>
              <div className="text-sm text-gray-400 whitespace-pre-wrap leading-relaxed border-t border-white/10 pt-3">
                {preview.body}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
